import axios from 'axios'

export interface BasketProduct {
  product_id: string
  quantity: number
}

export interface Basket {
  basket_id: string
  merchant_id: string
  name: string
  description: string
  original_value: number
  sale_price: number
  quantity_available: number
  pickup_start_time: string
  pickup_end_time: string
  is_active: boolean
  created_at: string
  updated_at: string
  merchant_name: string | null
  products: BasketProduct[]
}

export interface CreateBasketDto {
  merchant_id: string
  name: string
  description?: string
  original_value: number
  sale_price: number
  quantity_available: number
  pickup_start_time: string
  pickup_end_time: string
  products?: BasketProduct[]
}

export interface UpdateBasketDto {
  name?: string
  description?: string
  original_value?: number
  sale_price?: number
  quantity_available?: number
  pickup_start_time?: string
  pickup_end_time?: string
  is_active?: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface BasketFilters {
  merchant_id?: string
  is_active?: boolean
  available_only?: boolean
  search?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const basketApi = axios.create({
  baseURL: `${API_BASE_URL}/baskets`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
basketApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
basketApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const basketService = {
  // Get all baskets with optional filters
  async getAll(filters: BasketFilters = {}): Promise<PaginatedResponse<Basket>> {
    const params = new URLSearchParams()
    
    if (filters.merchant_id) params.append('merchant_id', filters.merchant_id)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.available_only !== undefined) params.append('available_only', filters.available_only.toString())
    if (filters.search) params.append('search', filters.search)

    const response = await basketApi.get('', { params })
    return response.data
  },

  // Get basket by ID
  async getById(id: string): Promise<{ success: boolean; data: Basket }> {
    const response = await basketApi.get(`/${id}`)
    return response.data
  },

  // Create new basket
  async create(basketData: CreateBasketDto): Promise<{ success: boolean; data: Basket; message: string }> {
    const response = await basketApi.post('', basketData)
    return response.data
  },

  // Update basket
  async update(id: string, basketData: UpdateBasketDto): Promise<{ success: boolean; data: Basket; message: string }> {
    const response = await basketApi.put(`/${id}`, basketData)
    return response.data
  },

  // Delete basket (soft delete)
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await basketApi.delete(`/${id}`)
    return response.data
  },

  // Update basket quantity
  async updateQuantity(id: string, quantity: number): Promise<{ success: boolean; data: Basket; message: string }> {
    const response = await basketApi.patch(`/${id}/quantity`, { quantity })
    return response.data
  },

  // Get basket statistics
  async getStatistics(): Promise<{
    total: number
    active: number
    inactive: number
    totalValue: number
    totalRevenue: number
    averageDiscount: number
  }> {
    const allBaskets = await this.getAll()
    const baskets = allBaskets.data
    
    const stats = {
      total: baskets.length,
      active: baskets.filter(b => b.is_active).length,
      inactive: baskets.filter(b => !b.is_active).length,
      totalValue: baskets.reduce((sum, b) => sum + b.original_value, 0),
      totalRevenue: baskets.reduce((sum, b) => sum + b.sale_price, 0),
      averageDiscount: baskets.length > 0 
        ? baskets.reduce((sum, b) => sum + ((b.original_value - b.sale_price) / b.original_value * 100), 0) / baskets.length
        : 0
    }
    
    return stats
  }
}

export default basketService
